import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import * as qs from "qs";
import { useDispatch, useSelector } from "react-redux";
import { updateAvailableCredits } from "../../../redux/actions/authActions";
import { useNavigate } from "react-router-dom";
import { filterQuery } from "../../../utils/helpers";
import { postRequest } from "../../../Apis";
import { contactListEndPoints } from "../../../utils/constants/httpConstants";
import { LOGIN_PATH } from "../../../utils/constants/routes";
import EventEmitter from "../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR, EMITTER_SUCCESS } from "../../../utils/constants";
import { ExportModal } from ".";
import CompanyEmployeesModal from "./CompanyEmployeesModal";
import CustomTable from "../../../components/Table/CustomTable";
import { paginationDataAction } from "../../../redux/actions/customTableActions";
import { STRINGS } from "../strings";
import { ICON_LABELS, fontWeight } from "../../../utils/constants/UI";
import TypographyInline from "../../../components/Typography/TypographyInline";
import { BRAND, GREY } from "../../../utils/constants/colors";
import FilterTab from "../../../components/FilterTab/FilterTab";
import CustomDialog from "../../../components/Dialog/CustomDialog";
import classes from "./LeadsComponent.module.css";

const CompanyTable = ({ heads, records, savedContactsTrue = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const allFiltersSelector = useSelector((state) => state.allFilters);
  const tableDataSelector = useSelector((state) => state.tableDataFetch);

  const paginationDataSelector = useSelector(
    (state) => state.getPaginationQuery,
  );
  const selector = useSelector((state) => state.ApplyUpdateColumn);
  const [selected, setSelected] = React.useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [columns] = useState(selector?.columns || heads);
  const [data, setData] = useState([]);
  const bulkSelected = false;
  const [employeesModal, setEmployeesModal] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [changedSearch, setChangedSearch] = useState("");

  const [selectedBulkValue, setSelectedBulkValue] = useState("");

  const queryObject = qs.parse(window.location?.search?.slice(1));

  const handleSaveToMyList = async (action) => {
    const payload = bulkSelected
      ? {
          ...filterQuery(allFiltersSelector?.payload),
          action_type: STRINGS.SAVE_LIST,
          list_id: STRINGS.MY_LIST,
        }
      : {
          record_ids: selected,
          action_type: STRINGS.SAVE_LIST,
          list_id: STRINGS.MY_LIST,
        };

    const URL = bulkSelected
      ? contactListEndPoints.companybulkSaveToList
      : contactListEndPoints.companySaveToList;
    try {
      const response = await postRequest(navigate, URL, payload);
      EventEmitter.emit(EMITTER_SUCCESS, response.data?.message || "");
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, err?.data?.message || STRINGS.SESSION);
    }
  };

  useEffect(() => {
    setData(records);
  }, [records]);

  useEffect(() => {
    if (changedSearch !== queryObject?.search) {
      setChangedSearch(queryObject?.search);
    }
  }, [queryObject, changedSearch]);

  useEffect(() => {
    if (paginationDataSelector?.data?.checkedRow) {
      setSelected(paginationDataSelector?.data?.checkedRow);
      setSelectedBulkValue("");
    } else {
      setSelected([]);
    }
  }, [paginationDataSelector?.data?.checkedRow]);

  // Clear selections whenever filter params change (excluding page/limit/sort/direction).
  const prevFilterRef = React.useRef(null);
  useEffect(() => {
    const { page, limit, sort, direction, ...filterOnly } =
      allFiltersSelector?.payload || {};
    const filterStr = JSON.stringify(filterOnly);
    if (prevFilterRef.current !== null && prevFilterRef.current !== filterStr) {
      dispatch(
        paginationDataAction({
          ...paginationDataSelector?.data,
          checkedRow: [],
        }),
      );
    }
    prevFilterRef.current = filterStr;
  }, [allFiltersSelector?.payload]);

  const handleUnselectAll = () => {
    dispatch(
      paginationDataAction({
        ...paginationDataSelector?.data,
        checkedRow: [],
      }),
    );
  };

  const handleUnlockEmail = async (row) => {
    try {
      const response = await postRequest(
        navigate,
        contactListEndPoints.singleEmailUnlock,
        { record_id: row?._id },
      );
      const newData = JSON.parse(JSON.stringify(data));
      const updatedRecord = newData?.filter((eachRecord) => {
        return eachRecord?._id === row?._id;
      })[0];
      updatedRecord.email = response?.data?.data?.email;
      const result = newData?.map((eachRecord) => {
        return eachRecord?._id === row?._id ? updatedRecord : eachRecord;
      });
      setData(result);
      dispatch(updateAvailableCredits(navigate));
      EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
    } catch (err) {
      console.log(err);
    }
  };

  const handleNavigateBack = (record) => {
    setCompanyName(record.company_name);
    setCompanyId(record._id);
    setEmployeesModal(true);
  };

  const tableData = {
    heads: heads?.filter((item) => item?.isChecked && item?.companyField),
    body: records,
    isSticky: true,
    total_records:
      tableDataSelector?.totalRecords > 1000
        ? 1000
        : tableDataSelector?.totalRecords,
    isLoading: tableDataSelector?.loading,
    header_strip_selected_value: selectedBulkValue,
    max_selection: 500,
    on_max_selection_reached: () =>
      EventEmitter.emit(
        EMITTER_ERROR,
        "You can only select a maximum of 500 companies at a time.",
      ),
    actions: {
      email: {
        function: (row) => handleUnlockEmail(row),
      },
      company_info: {
        function: (row, head, index) => {
          handleNavigateBack(row);
        },
      },
    },
  };

  const headerStrip = [
    !savedContactsTrue && (
      <FilterTab
        leftIcon={ICON_LABELS.FACT_CHECK}
        label={STRINGS.SAVE_COMPANIES}
        onClick={handleSaveToMyList}
      />
    ),
    <FilterTab
      leftIcon={ICON_LABELS.DOWNLOAD_FOR_ONLINE}
      label={STRINGS.EXPORT}
      onClick={() => {
        setShowExportModal(true);
      }}
    />,
  ];

  const dialogHeader = {
    heading: {
      id: STRINGS.HEADER_ID,
      label: companyName,
    },
  };

  return (
    <Box className={classes.tableBox}>
      <ExportModal
        open={showExportModal}
        setOpen={setShowExportModal}
        bulkSelected={bulkSelected}
        record_ids={selected}
        columns={selector?.columns ? selector?.columns : columns}
        leadType={STRINGS.COMPANY_SINGLE}
      />
      {/* All Contact Label */}
      {selected?.length >= 1 ? (
        <Box className={classes.containAllContacts}>
          <TypographyInline
            label={`Total ${selected.length} ${selected.length === 1 ? "company" : "companies"} selected.`}
            size={"sm"}
            fontWeight={fontWeight.REGULAR}
            color={GREY.SECONDARY}
          />
          <div className={classes.cursorPointer} onClick={handleUnselectAll}>
            <TypographyInline
              label={STRINGS.UNSELECT_ALL}
              size={"sm"}
              fontWeight={fontWeight.REGULAR}
              color={BRAND.PRIMARY}
            />
          </div>
        </Box>
      ) : null}
      {/* All Contact Label */}

      <div
        className={
          selected?.length >= 1
            ? classes.containTableSmall
            : classes.containTable
        }
      >
        <div className={classes.compTableContainer}>
          <CustomTable tableData={tableData} headerStrip={headerStrip} />
          <CustomDialog
            open={employeesModal}
            onClose={() => {
              setEmployeesModal(false);
              setShowUserDetailModal(false);
            }}
            header={dialogHeader}
            width={"30vw"}
            remove_padding
          >
            <CompanyEmployeesModal
              showUserDetailModal={showUserDetailModal}
              setShowUserDetailModal={setShowUserDetailModal}
              companyId={companyId}
              companyName={companyName}
              setEmployeesModal={setEmployeesModal}
              employeesModal={employeesModal}
              savedContactsTrue={savedContactsTrue}
            />
          </CustomDialog>
        </div>
      </div>
    </Box>
  );
};

export default React.memo(CompanyTable);
